/**
 * Turning a parsed design guide into the token set this app is built on.
 *
 * The server sends a small, safe summary of a guide — brand colour, accent,
 * surfaces, ink, fonts, a radius. The theme wants the fuller set the blue guide
 * ships with: five steps of the brand ramp, four inks, three surfaces. The
 * missing steps are computed from the ones that were given, so a guide that
 * defines four colours still produces a complete, coherent theme.
 *
 * Every value falls back to the built-in blue token, so a malformed or missing
 * payload degrades to today's appearance rather than to an unstyled page.
 */

const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));

const rgbOf = (value) => {
  const text = String(value || '').trim();
  if (text.startsWith('#')) {
    const hex = text.slice(1);
    const full = hex.length === 3 ? hex.split('').map((part) => part + part).join('') : hex.slice(0, 6);
    if (full.length !== 6 || /[^0-9a-f]/i.test(full)) return null;
    return [0, 2, 4].map((start) => parseInt(full.slice(start, start + 2), 16));
  }
  const parts = text.match(/[\d.]+/g);
  return parts && parts.length >= 3 ? parts.slice(0, 3).map(Number) : null;
};

const hexOf = (rgb) => `#${rgb.map((channel) => clamp(channel).toString(16).padStart(2, '0')).join('')}`;

export const mix = (from, to, amount) => {
  const a = rgbOf(from);
  const b = rgbOf(to);
  if (!a || !b) return from;
  return hexOf([0, 1, 2].map((index) => a[index] + (b[index] - a[index]) * amount));
};

const isColour = (value) => Boolean(rgbOf(value));

/** The colour at a given opacity, for tints and focus rings. */
export const alpha = (colour, amount) => {
  const rgb = rgbOf(colour);
  return rgb ? `rgba(${rgb.map(clamp).join(',')},${amount})` : colour;
};

const channelOf = (channel) => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG contrast ratio of two colours, 1 to 21. */
export const contrastOf = (a, b) => {
  const lum = (value) => {
    const rgb = rgbOf(value);
    if (!rgb) return null;
    const [r, g, bl] = rgb.map(channelOf);
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const first = lum(a);
  const second = lum(b);
  if (first === null || second === null) return 0;
  const [light, dark] = first > second ? [first, second] : [second, first];
  return (light + 0.05) / (dark + 0.05);
};

/**
 * White or the ink, whichever reads better on this colour — and if neither
 * reaches 4.5:1, the ink pushed towards black until it does. A mid blue is the
 * case: white manages 3.7:1 on it and the guide's navy ink 4.4:1.
 */
export const readableOn = (background, ink) => {
  const white = contrastOf('#FFFFFF', background);
  const own = contrastOf(ink, background);
  if (Math.max(white, own) >= 4.5) return white >= own ? '#FFFFFF' : ink;
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mix(ink, '#000000', step * 0.05);
    if (contrastOf(candidate, background) >= 4.5) return candidate;
  }
  return '#000000';
};

/**
 * The colour, darkened until it can be read as text on this background.
 *
 * A status colour that works as a fill often cannot be text: orange on white is
 * about 2.4:1. Rather than ship a second hand-picked orange, the one the guide
 * named is stepped towards black, no further than it needs to go.
 */
export const textShade = (colour, background = '#FFFFFF', target = 4.5) => {
  for (let step = 0; step <= 20; step += 1) {
    const candidate = mix(colour, '#000000', step * 0.05);
    if (contrastOf(candidate, background) >= target) return candidate;
  }
  return '#000000';
};

/**
 * @param base    the built-in blue tokens, used for anything not supplied
 * @param theme   what /api/public/appearance returned, or null
 */
export const tokensFrom = (base, theme) => {
  if (!theme?.tokens) return base;
  const supplied = theme.tokens;
  const dark = supplied.surfaces?.dark || {};
  const light = supplied.surfaces?.light || {};

  const primary = isColour(supplied.primary) ? supplied.primary : base.p500;
  const accent = isColour(supplied.accent) ? supplied.accent : base.c400;
  const darkBg = isColour(dark.bg) ? dark.bg : base.bg;
  const lightBg = isColour(light.bg) ? light.bg : base.paper;
  const darkInk = isColour(dark.ink) ? dark.ink : base.ink;
  const lightInk = isColour(light.ink) ? light.ink : base.inkOnPaper;

  return {
    ...base,

    bg: darkBg,
    bg2: isColour(dark.bg2) ? dark.bg2 : mix(darkBg, '#FFFFFF', 0.05),
    bg3: isColour(dark.bg3) ? dark.bg3 : mix(darkBg, '#FFFFFF', 0.12),
    navySurface: mix(darkBg, '#FFFFFF', 0.08),
    navy: mix(darkBg, primary, 0.35),
    paper: lightBg,

    // The brand ramp, stepped from the one colour the guide had to name.
    p50: mix(lightBg, primary, 0.06),
    p100: mix(lightBg, primary, 0.14),
    p200: mix(lightBg, primary, 0.3),
    p300: mix(primary, '#FFFFFF', 0.4),
    p400: mix(primary, '#FFFFFF', 0.2),
    p500: primary,
    p600: mix(primary, '#000000', 0.15),
    p700: mix(primary, '#000000', 0.3),
    p800: mix(primary, '#000000', 0.45),

    c300: mix(accent, '#FFFFFF', 0.35),
    c400: accent,
    c500: mix(accent, '#000000', 0.12),
    c600: mix(accent, '#000000', 0.28),

    // Ink steps fade towards the surface they sit on, which is what keeps
    // secondary and metadata text legible in either mode.
    ink: darkInk,
    ink2: mix(darkInk, darkBg, 0.25),
    ink3: mix(darkInk, darkBg, 0.45),
    ink4: mix(darkInk, darkBg, 0.62),

    inkOnPaper: lightInk,
    // A guide that names its supporting text colour gets exactly that.
    inkOnPaper2: isColour(supplied.neutral?.muted) ? supplied.neutral.muted : mix(lightInk, lightBg, 0.25),
    inkOnPaper3: isColour(supplied.neutral?.muted) ? supplied.neutral.muted : mix(lightInk, lightBg, 0.45),

    gradient1: `linear-gradient(135deg, ${accent} 0%, ${mix(primary, '#FFFFFF', 0.2)} 50%, ${mix(primary, '#000000', 0.15)} 100%)`,
    gradient2: `linear-gradient(120deg, #FFFFFF 0%, ${mix(lightBg, primary, 0.12)} 35%, ${mix(primary, '#FFFFFF', 0.4)} 70%, ${accent} 100%)`,

    r2: Number.isFinite(supplied.radius) && supplied.radius > 0 ? Math.min(supplied.radius, 28) : base.r2,

    danger: isColour(supplied.danger) ? supplied.danger : base.danger,
    success: isColour(supplied.success) ? supplied.success : '#4ADE80',
    warning: isColour(supplied.warning) ? supplied.warning : '#F59E0B',

    // What a guide says about the shape of things, not only the colour of them.
    // Null for a guide that says nothing, which is every guide before these.
    shape: supplied.shape && typeof supplied.shape === 'object' ? supplied.shape : null,
    neutral: {
      border: isColour(supplied.neutral?.border) ? supplied.neutral.border : null,
      subtle: isColour(supplied.neutral?.subtle) ? supplied.neutral.subtle : null,
      muted: isColour(supplied.neutral?.muted) ? supplied.neutral.muted : null
    },

    fontSans: supplied.fonts?.sans || null,
    fontMono: supplied.fonts?.mono || null,
    fontSerif: supplied.fonts?.serif || null
  };
};

export default tokensFrom;
